// Components map for @assistant-ui/react's MessagePrimitive.Parts that the
// Gateway thread uses to turn raw content blocks (text / reasoning /
// tool-call) into hub-flavoured UI: markdown body, mini-tables for tabular
// tool results, citation chips, quarantine cards for external untrusted
// content, collapsible JSON cards for everything else.

import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import {
  MessagePrimitive,
  useMessagePartText,
  useMessagePartReasoning,
} from '@assistant-ui/react';
import { HubMarkdownBody } from './shared.jsx';
import {
  tryParseJson,
  summarizeJson,
  toolNameTaxonomy,
  inferToolNameFromValue,
  isMiniTableShape,
  pickTableColumns,
  extractCitations,
  splitExternalQuarantine,
  truncateAt,
  extractShellCommand,
} from './gatewayFormat.js';

const MAX_TABLE_ROWS = 6;
const RAW_TRUNCATE = 1200;

// ── Visual primitives ────────────────────────────────────────────────────

// Inline feather-style icons for each tool family — no emoji. Keys match
// toolNameTaxonomy().icon. stroke=currentColor so the .is-<family> accent
// color applies.
const TOOL_GLYPH_ICONS = {
  terminal: <><polyline points="4 17 10 11 4 5" /><line x1="12" y1="19" x2="20" y2="19" /></>,
  globe: <><circle cx="12" cy="12" r="9" /><line x1="3" y1="12" x2="21" y2="12" /><path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" /></>,
  'download-cloud': <><path d="M8 17a5 5 0 1 1 1-9.9A6 6 0 0 1 20 10a4 4 0 0 1-2 7" /><polyline points="8.5 14.5 12 18 15.5 14.5" /><line x1="12" y1="11" x2="12" y2="18" /></>,
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
  eye: <><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" /><circle cx="12" cy="12" r="3" /></>,
  tool: <><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.1-.5-.5-2.1 2.6-2.6Z" /></>,
};

function ToolGlyph({ name }) {
  const { icon, family } = toolNameTaxonomy(name);
  return (
    <span className={`hub-gw2-tool-glyph is-${family}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {TOOL_GLYPH_ICONS[icon] || TOOL_GLYPH_ICONS.tool}
      </svg>
    </span>
  );
}

function MiniTable({ items, columns }) {
  const cols = columns && columns.length ? columns : pickTableColumns(items);
  const rows = items.slice(0, MAX_TABLE_ROWS);
  const overflow = items.length - rows.length;
  return (
    <div className="hub-gw2-mini-table-wrap">
      <table className="hub-gw2-mini-table">
        <thead>
          <tr>
            {cols.map((c) => <th key={c}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {cols.map((c) => {
                const v = row?.[c];
                const cell = v == null
                  ? ''
                  : typeof v === 'object' ? JSON.stringify(v) : String(v);
                return <td key={c} title={cell}>{cell.length > 60 ? cell.slice(0, 59) + '…' : cell}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {overflow > 0 && (
        <div className="hub-gw2-mini-table-overflow">+ {overflow} more row{overflow === 1 ? '' : 's'}</div>
      )}
    </div>
  );
}

function CitationChips({ citations }) {
  if (!citations?.length) return null;
  return (
    <div className="hub-gw2-citations" role="list">
      {citations.map((c, i) => (
        <a
          key={`${c.url}-${i}`}
          className="hub-gw2-citation-chip"
          href={c.url}
          target="_blank"
          rel="noopener noreferrer"
          role="listitem"
          title={c.url}
        >
          {c.title}
        </a>
      ))}
    </div>
  );
}

function ExternalQuarantineCard({ id, source, body }) {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="hub-gw2-quarantine"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary>
        <span className="hub-gw2-quarantine-badge" aria-hidden="true">⚠</span>
        <span className="hub-gw2-quarantine-label">External content</span>
        {source && <span className="hub-gw2-quarantine-source">· {source}</span>}
        {id && <span className="hub-gw2-quarantine-id">· {id}</span>}
      </summary>
      <pre className="hub-gw2-quarantine-body">{body || '(empty)'}</pre>
    </details>
  );
}

function JsonValueCard({ value, name }) {
  const [open, setOpen] = useState(false);
  const summary = useMemo(() => summarizeJson(value), [value]);
  const citations = useMemo(() => extractCitations(value), [value]);
  const tableShape = useMemo(
    () => (Array.isArray(value) ? isMiniTableShape(value) : false),
    [value],
  );
  const tableColumns = useMemo(
    () => (tableShape ? pickTableColumns(value) : []),
    [tableShape, value],
  );
  const inferredName = name || inferToolNameFromValue(value);
  const fullText = useMemo(() => JSON.stringify(value, null, 2), [value]);

  return (
    <div className="hub-gw2-json-card">
      <div className="hub-gw2-json-card-head">
        {inferredName && <ToolGlyph name={inferredName} />}
        <span className="hub-gw2-json-card-summary">{summary}</span>
        <button
          type="button"
          className="hub-gw2-json-card-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? 'Hide' : 'View'}
        </button>
      </div>
      {tableShape && <MiniTable items={value} columns={tableColumns} />}
      {citations.length > 0 && <CitationChips citations={citations} />}
      {open && (
        <pre className="hub-gw2-json-card-raw">{truncateAt(fullText, RAW_TRUNCATE)}</pre>
      )}
    </div>
  );
}

// ── JSON visualizer ──────────────────────────────────────────────────────
// Render a parsed JSON payload as a clean key/value tree on a white surface
// instead of a raw code dump. Objects become labelled rows, tabular arrays
// reuse the mini-table, other arrays become an indexed list, and scalars are
// typed (string / number / bool / null / url). Depth- and length-capped so a
// huge payload can't blow up the card.
const JV_MAX_DEPTH = 5;
const JV_MAX_ITEMS = 12;
const JV_STR_MAX = 400;

function isPlainObject(v) {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function JvScalar({ value }) {
  if (value === null || value === undefined) return <span className="hub-gw2-jv-null">null</span>;
  if (typeof value === 'boolean') return <span className={`hub-gw2-jv-bool is-${value}`}>{String(value)}</span>;
  if (typeof value === 'number') return <span className="hub-gw2-jv-num">{String(value)}</span>;
  const s = String(value);
  if (!s.trim()) return <span className="hub-gw2-jv-empty">empty</span>;
  if (/^https?:\/\/\S+$/i.test(s.trim())) {
    return <a className="hub-gw2-jv-link" href={s.trim()} target="_blank" rel="noopener noreferrer">{s.trim()}</a>;
  }
  const shown = s.length > JV_STR_MAX ? s.slice(0, JV_STR_MAX - 1) + '…' : s;
  return <span className="hub-gw2-jv-str" title={s.length > JV_STR_MAX ? s : undefined}>{shown}</span>;
}

function JvValue({ value, depth, flat = false }) {
  // Past the depth cap, fall back to a compact one-line summary.
  if (depth >= JV_MAX_DEPTH && value && typeof value === 'object') {
    return <span className="hub-gw2-jv-str">{summarizeJson(value)}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="hub-gw2-jv-empty">empty list</span>;
    // `flat` (the results dialog) renders arrays as an indented list, never a
    // mini-table — the roomy dialog reads better as a tree than a cramped grid.
    if (!flat && isMiniTableShape(value)) return <MiniTable items={value} columns={pickTableColumns(value)} />;
    const shown = value.slice(0, JV_MAX_ITEMS);
    return (
      <div className="hub-gw2-jv-list">
        {shown.map((item, i) => (
          <div className="hub-gw2-jv-list-item" key={i}>
            <span className="hub-gw2-jv-idx">{i + 1}</span>
            <div className="hub-gw2-jv-list-val">
              {(isPlainObject(item) || Array.isArray(item))
                ? <JvValue value={item} depth={depth + 1} flat={flat} />
                : <JvScalar value={item} />}
            </div>
          </div>
        ))}
        {value.length > JV_MAX_ITEMS && (
          <div className="hub-gw2-jv-more">+ {value.length - JV_MAX_ITEMS} more</div>
        )}
      </div>
    );
  }
  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) return <span className="hub-gw2-jv-empty">empty</span>;
    return (
      <div className={`hub-gw2-jv-obj${depth > 0 ? ' is-nested' : ''}`}>
        {entries.map(([k, v]) => {
          const nested = (isPlainObject(v) && Object.keys(v).length > 0)
            || (Array.isArray(v) && v.length > 0 && (flat || !isMiniTableShape(v)));
          return (
            <div className={`hub-gw2-jv-row${nested ? ' is-block' : ''}`} key={k}>
              <span className="hub-gw2-jv-key" title={k}>{k}</span>
              <div className="hub-gw2-jv-val">
                {(isPlainObject(v) || Array.isArray(v))
                  ? <JvValue value={v} depth={depth + 1} flat={flat} />
                  : <JvScalar value={v} />}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  return <JvScalar value={value} />;
}

function JsonView({ value, flat = false }) {
  return (
    <div className="hub-gw2-jv">
      <JvValue value={value} depth={0} flat={flat} />
    </div>
  );
}

// ── Thought gate ──────────────────────────────────────────────────────────
// Client-side sequencing: the thought (reasoning) part plays FIRST — types out,
// then curls — and the message body (text + tool cards) stays hidden until it
// finishes. The reserve-slot ordering guarantees the reasoning part renders
// before the body, so ThinkingPanel can mark the gate DURING render (before the
// body parts render) — no flash. Messages with no reasoning reveal immediately.
const ThoughtGateContext = React.createContext(null);
function ThoughtGateProvider({ children }) {
  const [revealed, setRevealed] = useState(false);
  const gatedRef = useRef(false);
  const value = useMemo(() => ({
    revealed,
    markGated() { gatedRef.current = true; },
    isGated() { return gatedRef.current; },
    reveal() { setRevealed(true); },
  }), [revealed]);
  // Nothing gated the body by the time effects run (e.g. a user message or a
  // turn with no reasoning) → reveal it.
  useEffect(() => { if (!gatedRef.current) setRevealed(true); });
  return <ThoughtGateContext.Provider value={value}>{children}</ThoughtGateContext.Provider>;
}
function useThoughtGate() {
  return useContext(ThoughtGateContext) || { revealed: true, markGated() {}, isGated() { return false; }, reveal() {} };
}

// The thought plays first: while empty it's an animated "Thinking" bubble; when
// the reasoning lands it TYPES OUT (smooth rAF typewriter, capped ~200 chars),
// then CURLS into the compact Thought bubble. While it plays it marks the gate
// so the body stays hidden, and reveals the body when done.
const THINK_CAP = 200;
const THINK_MS_PER_CHAR = 16;   // ~3.2s for a full 200-char thought
const THINK_MIN_MS = 500;
function ThinkingPanel({ text, streaming }) {
  const gate = useThoughtGate();
  const safe = String(text || '').trim();
  const pending = !safe;
  const capped = safe.slice(0, THINK_CAP);
  // Only a LIVE turn (running status at mount) plays the typewriter and gates the
  // body. Historical thoughts loaded from an existing chat render collapsed
  // immediately — no replaying the animation, no holding the body.
  const [liveOnMount] = useState(streaming);
  const [phase, setPhase] = useState(liveOnMount ? 'pending' : 'done');
  const [typed, setTyped] = useState(0);
  const [userOpen, setUserOpen] = useState(false);
  const startedRef = useRef(false);

  // Smooth rAF typewriter with an easeOut curve (no coarse setInterval steps).
  useEffect(() => {
    if (!liveOnMount || !safe || startedRef.current) return;
    startedRef.current = true;
    setPhase('typing');
    setTyped(0);
    const total = capped.length;
    const dur = Math.max(THINK_MIN_MS, total * THINK_MS_PER_CHAR);
    const ease = (t) => 1 - Math.pow(1 - t, 2); // easeOutQuad
    let raf = 0;
    let start = null;
    const tick = (now) => {
      if (start == null) start = now;
      const t = Math.min(1, (now - start) / dur);
      setTyped(Math.round(ease(t) * total));
      if (t < 1) raf = requestAnimationFrame(tick);
      else raf = requestAnimationFrame(() => setTimeout(() => setPhase('done'), 220));
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [safe, liveOnMount]); // eslint-disable-line react-hooks/exhaustive-deps

  const isTyping = phase === 'typing';
  const isDone = phase === 'done';

  // Gate the body ONLY while a LIVE thought is still playing; reveal it once the
  // thought is done, or if this bubble unmounts (placeholder dropped). Historical
  // thoughts never gate (liveOnMount is false), so old chats show instantly.
  if (liveOnMount && !isDone) gate.markGated();
  useEffect(() => { if (isDone) gate.reveal(); }, [isDone]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => gate.reveal(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const open = isTyping || (isDone && userOpen);
  const label = isDone ? 'Thought' : 'Thinking';
  const preview = safe.length > 140 ? safe.slice(0, 139) + '…' : safe;

  return (
    <div className={`hub-gw2-thinking${streaming ? ' is-streaming' : ''}${pending ? ' is-pending' : ''}${isTyping ? ' is-typing' : ''}`} data-open={open ? 'true' : 'false'}>
      <button
        type="button"
        className="hub-gw2-thinking-summary"
        onClick={() => { if (isDone) setUserOpen((v) => !v); }}
        aria-expanded={open}
        disabled={!isDone}
      >
        <span className="hub-gw2-thinking-label">{label}</span>
        {pending && <span className="hub-gw2-thinking-dots" aria-hidden="true"><span /><span /><span /></span>}
        {isDone && !userOpen && preview && <span className="hub-gw2-thinking-preview">· {preview}</span>}
      </button>
      {!pending && (
        <div className="hub-gw2-reveal" data-open={open ? 'true' : 'false'}>
          <div className="hub-gw2-reveal-in">
            <pre className="hub-gw2-thinking-body">
              {isTyping ? capped.slice(0, typed) : safe}
              {isTyping && <span className="hub-gw2-type-caret" aria-hidden="true" />}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

// ── AUI part components ──────────────────────────────────────────────────

function GwTextPart() {
  const { text, status } = useMessagePartText();
  const gate = useThoughtGate();
  const streaming = status?.type === 'running';
  const safe = typeof text === 'string' ? text : '';

  // Held behind the thought until it finishes playing.
  if (!gate.revealed && gate.isGated()) return null;

  if (!safe) {
    if (streaming) {
      return (
        <div className="hub-gw2-pending" aria-label="thinking">
          <span className="hub-gw2-pending-label">Thinking</span>
          <span className="hub-gw2-pending-dots" aria-hidden="true">
            <span /><span /><span />
          </span>
        </div>
      );
    }
    return null;
  }

  // External untrusted-content quarantine: split into intro/cards/outro and
  // render each block in its own collapsible card so the markdown body stays
  // clean.
  const quar = splitExternalQuarantine(safe);
  if (quar) {
    return (
      <div className="hub-gw2-text">
        {quar.map((p, i) => p.kind === 'quarantine'
          ? <ExternalQuarantineCard key={`q-${i}`} id={p.id} source={p.source} body={p.body} />
          : <HubMarkdownBody key={`t-${i}`} text={p.text} streaming={streaming && i === quar.length - 1} />
        )}
      </div>
    );
  }

  // Raw JSON sneaks into history when the model paraphrases a tool result
  // back into the response. Don't dump it as a markdown wall.
  const json = tryParseJson(safe);
  if (json) {
    return <JsonValueCard value={json} />;
  }

  return <HubMarkdownBody text={safe} streaming={streaming} />;
}

function GwReasoningPart() {
  const { text, status } = useMessagePartReasoning();
  return <ThinkingPanel text={text || ''} streaming={status?.type === 'running'} />;
}

// Lightweight per-bubble step counter: number every tool-call part that
// renders inside the same message so a chain of 5 execs reads as
// "exec #1, exec #2, exec #3" instead of five identical cards. AUI
// doesn't pass sibling part index to component overrides, so we hash
// the toolCallId into a stable monotonic per-mount registry.
const toolStepRegistry = new Map();
let toolStepCounter = 0;
function stepForToolCall(toolCallId) {
  if (!toolCallId) return null;
  const existing = toolStepRegistry.get(toolCallId);
  if (existing != null) return existing;
  toolStepCounter += 1;
  const n = toolStepCounter;
  toolStepRegistry.set(toolCallId, n);
  return n;
}

// Direct, synchronous JSON token highlighter — no markdown pipeline. Colors
// keys, strings, numbers, booleans/null and punctuation so the command's
// structure (which key is the command, which are its args) reads at a glance.
const JSON_TOKEN_RE = /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}[\],])/g;
function highlightJson(src) {
  const out = [];
  let last = 0;
  let i = 0;
  let m;
  JSON_TOKEN_RE.lastIndex = 0;
  while ((m = JSON_TOKEN_RE.exec(src)) !== null) {
    if (m.index > last) out.push(src.slice(last, m.index));
    if (m[1] !== undefined) {
      // A quoted string is a key when a colon follows, otherwise a value.
      out.push(<span key={i++} className={m[2] ? 'hljson-key' : 'hljson-str'}>{m[1]}</span>);
      if (m[2]) out.push(m[2]);
    } else if (m[3] !== undefined) {
      out.push(<span key={i++} className="hljson-bool">{m[3]}</span>);
    } else if (m[4] !== undefined) {
      out.push(<span key={i++} className="hljson-num">{m[4]}</span>);
    } else if (m[5] !== undefined) {
      out.push(<span key={i++} className="hljson-punc">{m[5]}</span>);
    }
    last = JSON_TOKEN_RE.lastIndex;
  }
  if (last < src.length) out.push(src.slice(last));
  return out;
}

// The tool "command" (its arguments) shown as syntax-highlighted code,
// truncated to the first 2 lines with an ellipsis and a Show more toggle.
// Only the command gets this treatment — the result keeps its own view.
function CommandBlock({ text }) {
  const [more, setMore] = useState(false);
  const clean = String(text ?? '').replace(/\s+$/, '');
  const lines = clean.split('\n');
  const overflow = lines.length > 2;
  const shown = more || !overflow ? clean : `${lines.slice(0, 2).join('\n')}\n…`;
  const isJson = tryParseJson(clean) != null;
  return (
    <div className="hub-gw2-tool-cmd">
      <pre className="hub-gw2-tool-cmd-code"><code>{isJson ? highlightJson(shown) : shown}</code></pre>
      {overflow && (
        <button type="button" className="hub-gw2-tool-more" onClick={() => setMore((v) => !v)}>
          {more ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

// Fish-shell-flavoured command highlighter. A pragmatic tokenizer (not a full
// bash parser): it classifies quoted strings, $variables, operators/pipes,
// -flags, filesystem paths, the main command word, and plain args, so an exec
// call reads like a real terminal line. The "main command" — the first word,
// and the first word after a pipe/;/&&/subshell separator — gets the shimmer.
const SHELL_TOKEN_RE = /(\s+)|("(?:\\.|[^"\\])*"|'[^']*')|(\$\{[^}]*\}|\$\w+)|(&&|\|\||\$\(|[|;&()<>]+)|(-{1,2}[A-Za-z0-9][\w-]*)|([^\s|;&()<>'"]+)/g;
function looksLikePath(word) {
  return /[/~]/.test(word) || /^[A-Za-z0-9._-]+:[^\s]/.test(word);
}
function highlightShell(src) {
  const out = [];
  let last = 0;
  let i = 0;
  let expectCommand = true; // next bare word is the command
  let m;
  SHELL_TOKEN_RE.lastIndex = 0;
  while ((m = SHELL_TOKEN_RE.exec(src)) !== null) {
    if (m.index > last) out.push(src.slice(last, m.index));
    if (m[1] !== undefined) {
      out.push(m[1]); // whitespace, verbatim
    } else if (m[2] !== undefined) {
      out.push(<span key={i++} className="fsh-str">{m[2]}</span>);
    } else if (m[3] !== undefined) {
      out.push(<span key={i++} className="fsh-var">{m[3]}</span>);
    } else if (m[4] !== undefined) {
      out.push(<span key={i++} className="fsh-op">{m[4]}</span>);
      // A command separator (pipe / ; / & / subshell) means the next bare
      // word starts a fresh command. Redirections (>, >>, <) do not.
      if (/[|;&(]/.test(m[4])) expectCommand = true;
    } else if (m[5] !== undefined) {
      out.push(<span key={i++} className="fsh-flag">{m[5]}</span>);
    } else if (m[6] !== undefined) {
      if (expectCommand) {
        out.push(<span key={i++} className="fsh-cmd">{m[6]}</span>);
        expectCommand = false;
      } else if (looksLikePath(m[6])) {
        out.push(<span key={i++} className="fsh-path">{m[6]}</span>);
      } else {
        out.push(<span key={i++} className="fsh-arg">{m[6]}</span>);
      }
    }
    last = SHELL_TOKEN_RE.lastIndex;
  }
  if (last < src.length) out.push(src.slice(last));
  return out;
}

// The actual Linux command for an exec-style tool call, rendered as a tiny
// fish-shell terminal instead of a raw JSON envelope. Truncates to the first
// 2 lines with a Show more toggle, matching CommandBlock.
function ShellCommand({ command }) {
  const [more, setMore] = useState(false);
  const clean = String(command ?? '').replace(/\s+$/, '');
  const lines = clean.split('\n');
  const overflow = lines.length > 2;
  const shown = more || !overflow ? clean : `${lines.slice(0, 2).join('\n')}\n…`;
  return (
    <div className="hub-gw2-shell">
      <pre className="hub-gw2-shell-body"><span className="hub-gw2-shell-prompt"><span className="fsh-lobster">🦞</span> <span className="fsh-pwd">~/hub</span> <span className="fsh-arrow">❯</span> </span><code>{highlightShell(shown)}</code></pre>
      {overflow && (
        <button type="button" className="hub-gw2-tool-more hub-gw2-shell-more" onClick={() => setMore((v) => !v)}>
          {more ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

// Short, dim descriptor for the "View output" button — row/field/line count.
function resultMeta(resultValue, resultIsJson) {
  if (resultValue == null) return '';
  if (resultIsJson) {
    if (Array.isArray(resultValue)) return `${resultValue.length} row${resultValue.length === 1 ? '' : 's'}`;
    if (typeof resultValue === 'object') {
      const k = Object.keys(resultValue).length;
      return k ? `${k} field${k === 1 ? '' : 's'}` : '';
    }
    return '';
  }
  const text = typeof resultValue === 'string' ? resultValue : (resultValue.text || '');
  const lines = String(text).split('\n').filter((l) => l.trim().length).length;
  return lines ? `${lines} line${lines === 1 ? '' : 's'}` : '';
}

// Tool result shown in a monochrome, softly-lit modal (portaled to <body>)
// instead of an inline table — results are verbose and the reveal card should
// stay compact. Full result, scrollable; Esc / backdrop / × to close; Copy.
// Legibility highlighter for raw terminal output (docker ps, ls -la, logs).
// Only SPECIFIC tokens get a class — quoted strings, URLs, IP[:port], hex ids,
// numbers, ALL-CAPS headers, and up/exited-style status words. Everything else
// (and ALL whitespace) is emitted verbatim so column alignment is untouched.
const TERM_OK = /^(up|running|healthy|active|ready|ok|done|success|online|listening|enabled|passed|pass|true)$/i;
const TERM_BAD = /^(exited|error|errors|failed|failure|fatal|dead|denied|unhealthy|down|offline|disabled|warn|warning|critical|refused|timeout|false)$/i;
const TERM_TOKEN_RE = /("(?:\\.|[^"\\])*"|'[^']*')|(https?:\/\/[^\s'"]+)|(\b\d{1,3}(?:\.\d{1,3}){3}(?::\d+)?\b)|(\b[0-9a-f]{7,}\b)|(\b[A-Z][A-Z0-9_]{1,}\b)|(\b[A-Za-z]{2,}\b)|(\b\d+(?:\.\d+)?\b)/g;
const TERM_MAX = 40000; // skip highlighting for very large dumps (perf)
function highlightTerminal(src) {
  const text = String(src ?? '');
  if (text.length > TERM_MAX) return text;
  const out = [];
  let last = 0;
  let i = 0;
  let m;
  TERM_TOKEN_RE.lastIndex = 0;
  while ((m = TERM_TOKEN_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      out.push(<span key={i++} className="tout-str">{m[1]}</span>);
    } else if (m[2] !== undefined) {
      out.push(<span key={i++} className="tout-url">{m[2]}</span>);
    } else if (m[3] !== undefined) {
      out.push(<span key={i++} className="tout-ip">{m[3]}</span>);
    } else if (m[4] !== undefined) {
      out.push(<span key={i++} className="tout-hash">{m[4]}</span>);
    } else if (m[5] !== undefined) {
      // ALL-CAPS token — a status word (Up/EXITED) wins, else a column header.
      if (TERM_OK.test(m[5])) out.push(<span key={i++} className="tout-ok">{m[5]}</span>);
      else if (TERM_BAD.test(m[5])) out.push(<span key={i++} className="tout-bad">{m[5]}</span>);
      else out.push(<span key={i++} className="tout-head">{m[5]}</span>);
    } else if (m[6] !== undefined) {
      // Mixed/lower-case word — only colour recognised status words.
      if (TERM_OK.test(m[6])) out.push(<span key={i++} className="tout-ok">{m[6]}</span>);
      else if (TERM_BAD.test(m[6])) out.push(<span key={i++} className="tout-bad">{m[6]}</span>);
      else out.push(m[6]);
    } else if (m[7] !== undefined) {
      out.push(<span key={i++} className="tout-num">{m[7]}</span>);
    }
    last = TERM_TOKEN_RE.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function ResultDialog({ toolName, step, isError, resultValue, resultIsJson, rawText, onClose }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const copy = () => {
    try {
      navigator.clipboard.writeText(rawText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* clipboard unavailable */ }
  };
  return createPortal(
    <div className="hub-gw2-resdlg-overlay" onClick={onClose}>
      <div className="hub-gw2-resdlg" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="hub-gw2-resdlg-head">
          <span className={`hub-gw2-resdlg-badge${isError ? ' is-error' : ''}`} aria-hidden="true">
            {isError ? (
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            )}
          </span>
          <span className="hub-gw2-resdlg-title">{toolName || 'tool'}</span>
          <span className="hub-gw2-resdlg-sub">{step != null ? `#${step} · output` : 'output'}</span>
          <button type="button" className="hub-gw2-resdlg-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="hub-gw2-resdlg-body">
          {resultIsJson
            ? <JsonView value={resultValue} flat />
            : <pre className="hub-gw2-resdlg-raw">{highlightTerminal(typeof resultValue === 'string' ? resultValue : (resultValue.text || ''))}</pre>}
        </div>
        <div className="hub-gw2-resdlg-foot">
          <button type="button" className="hub-gw2-resdlg-copy" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function GwToolCallPart({ toolName, toolCallId, args, argsText, result, isError, status }) {
  // One reveal toggle for the whole card — args and result share a single
  // expanded panel, keeping the collapsed card as minimal as possible.
  const gate = useThoughtGate();
  const [open, setOpen] = useState(false);
  const taxonomy = toolNameTaxonomy(toolName);
  // Per-card done state, NOT message-level streaming state.
  // Each tool flips to "done" as soon as its own result lands, even if
  // the rest of the assistant turn is still in flight. The AUI
  // status?.type === 'running' signal reflects the whole message, so
  // ORing it in here was making every card stay on the spinner until
  // the entire run ended — that's the all-at-once flip the operator
  // saw. result !== undefined is the only truth signal that matters
  // for this specific tool.
  const running = result === undefined;
  void status;

  // Detect "no useful args yet" — the gateway emits args at tool start, but
  // openai-completions tool calls have their args streamed across deltas
  // and may arrive empty / pending. Treat undefined / null / {} / "" as
  // "still loading args" rather than "args = empty object".
  const hasArgs = useMemo(() => {
    if (args && typeof args === 'object') return Object.keys(args).length > 0;
    if (typeof argsText === 'string') return argsText.trim().length > 0;
    return false;
  }, [args, argsText]);

  const argsSummary = useMemo(() => {
    if (args && typeof args === 'object' && Object.keys(args).length > 0) return summarizeJson(args);
    if (argsText) return truncateAt(String(argsText).replace(/\s+/g, ' '), 80);
    return running ? 'preparing…' : '';
  }, [args, argsText, running]);

  const argsRaw = useMemo(() => {
    if (typeof argsText === 'string') return argsText;
    try { return JSON.stringify(args ?? {}, null, 2); } catch { return String(args); }
  }, [args, argsText]);

  // Exec/shell calls render their actual command in a fish-style terminal
  // rather than the raw JSON envelope. Non-exec calls stay on CommandBlock.
  const shellCmd = useMemo(() => extractShellCommand(args, argsText), [args, argsText]);

  // Results open in a modal (below) rather than an inline table.
  const [outOpen, setOutOpen] = useState(false);

  // Try to coerce the result into a friendlier shape: parse if it's a JSON
  // string, then route to a JSON card / mini-table.
  const resultValue = useMemo(() => {
    if (result == null) return null;
    if (typeof result === 'string') {
      const parsed = tryParseJson(result);
      return parsed != null ? parsed : { text: result };
    }
    return result;
  }, [result]);

  const resultIsJson = resultValue && typeof resultValue === 'object' && !('text' in resultValue && Object.keys(resultValue).length === 1);

  // Held behind the thought until it finishes playing.
  if (!gate.revealed && gate.isGated()) return null;

  return (
    <div className={`hub-gw2-tool${isError ? ' is-error' : ''}${running ? ' is-running' : ''}`}>
      <div className="hub-gw2-tool-head">
        <ToolGlyph name={toolName} />
        <span className="hub-gw2-tool-name">{toolName || 'tool'}</span>
        {(() => {
          const step = stepForToolCall(toolCallId);
          return step != null
            ? <span className="hub-gw2-tool-step" aria-label={`step ${step}`}>#{step}</span>
            : null;
        })()}
        {argsSummary && <span className="hub-gw2-tool-args-summary">({argsSummary})</span>}
        {/* Status is icon-only: a bare loader while running, a check when done,
            an x on error. No text label. */}
        <span
          className="hub-gw2-tool-status"
          role="img"
          aria-label={running ? 'running' : isError ? 'error' : 'done'}
          title={running ? 'running' : isError ? 'error' : 'done'}
        >
          {running ? (
            <span className="hub-gw2-tool-spinner" aria-hidden="true" />
          ) : isError ? (
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          )}
        </span>
        {(hasArgs || result !== undefined) && (
          <button
            type="button"
            className="hub-gw2-tool-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? 'Hide' : 'Reveal'}
          </button>
        )}
      </div>
      {(hasArgs || resultValue != null) && (
        <div className="hub-gw2-reveal" data-open={open ? 'true' : 'false'}>
          <div className="hub-gw2-reveal-in">
            <div className="hub-gw2-tool-reveal">
              {hasArgs && (
                <div className="hub-gw2-tool-section">
                  <div className="hub-gw2-tool-section-label">Command</div>
                  {shellCmd != null
                    ? <ShellCommand command={shellCmd} />
                    : <CommandBlock text={argsRaw} />}
                </div>
              )}
              {resultValue != null && (
                <div className="hub-gw2-tool-section">
                  <button
                    type="button"
                    className="hub-gw2-out-btn"
                    onClick={() => setOutOpen(true)}
                  >
                    <span>View output</span>
                    {resultMeta(resultValue, resultIsJson) && (
                      <span className="hub-gw2-out-count">· {resultMeta(resultValue, resultIsJson)}</span>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {outOpen && resultValue != null && (
        <ResultDialog
          toolName={toolName}
          step={stepForToolCall(toolCallId)}
          isError={isError}
          resultValue={resultValue}
          resultIsJson={resultIsJson}
          rawText={typeof result === 'string' ? result : (() => { try { return JSON.stringify(resultValue, null, 2); } catch { return String(resultValue); } })()}
          onClose={() => setOutOpen(false)}
        />
      )}
    </div>
  );
}

// Shown when the assistant message has zero parts — i.e. the run just
// started and no text / tool / reasoning events have landed yet. AUI
// removes this automatically once the first part appears.
function GwEmptyPart({ status }) {
  if (status?.type !== 'running') return null;
  return (
    <div className="hub-gw2-pending" aria-label="thinking">
      <span className="hub-gw2-pending-label">Thinking</span>
      <span className="hub-gw2-pending-dots" aria-hidden="true">
        <span /><span /><span />
      </span>
    </div>
  );
}

const gwComponents = {
  Empty: GwEmptyPart,
  Text: GwTextPart,
  Reasoning: GwReasoningPart,
  tools: { Override: GwToolCallPart },
};

export function GwMessageParts() {
  // ThoughtGateProvider sequences each message: the thought plays first, then
  // the body (text + tools) is revealed. No-reasoning messages reveal at once.
  return (
    <ThoughtGateProvider>
      <MessagePrimitive.Parts components={gwComponents} />
    </ThoughtGateProvider>
  );
}
